import { browser } from "$app/env";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api/v1";

/** Structured error from the backend's web.WriteError envelope. */
export class ApiError extends Error {
	code: string;
	status: number;
	details: unknown;
	traceId?: string;

	constructor(
		message: string,
		code: string,
		status: number,
		details: unknown = null,
		traceId?: string,
	) {
		super(message);
		this.name = "ApiError";
		this.code = code;
		this.status = status;
		this.details = details;
		this.traceId = traceId;
	}

	isAuthError(): boolean {
		return this.status === 401 || this.code === "UNAUTHORIZED";
	}

	isForbidden(): boolean {
		return this.status === 403 || this.code === "FORBIDDEN";
	}

	static networkError(): ApiError {
		return new ApiError("Network error — could not reach the server", "NETWORK_ERROR", 0);
	}
}

interface ErrorEnvelope {
	code?: string;
	message?: string;
	details?: unknown;
	trace_id?: string;
	request_id?: string;
	// legacy {error} shape
	error?: string;
}

export function getCsrfToken(): string | undefined {
	if (!browser) return undefined;
	const match = document.cookie.match(/(?:^|; )csrf_=([^;]*)/);
	return match ? decodeURIComponent(match[1]) : undefined;
}

interface RequestOptions {
	headers?: Record<string, string>;
	signal?: AbortSignal;
}

async function api<T>(
	endpoint: string,
	options: RequestOptions | undefined,
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
	body?: unknown,
): Promise<T> {
	const url = `${API_BASE_URL}${endpoint}`;

	const isStateChanging = method !== "GET";
	const csrfToken = isStateChanging ? getCsrfToken() : undefined;

	const headers: Record<string, string> = {
		Accept: "application/json",
		...(body !== undefined && !(body instanceof FormData)
			? { "Content-Type": "application/json" }
			: {}),
		...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
		...(options?.headers ?? {}),
	};

	let response: Response;
	try {
		response = await fetch(url, {
			method,
			headers,
			credentials: "include",
			signal: options?.signal,
			...(body !== undefined
				? { body: body instanceof FormData ? body : JSON.stringify(body) }
				: {}),
		});
	} catch (err) {
		if ((err as Error).name === "AbortError") throw err;
		throw ApiError.networkError();
	}

	if (!response.ok) {
		const envelope = (await response
			.json()
			.catch(() => ({ message: `HTTP ${response.status}` }))) as ErrorEnvelope;
		const message =
			envelope.message ??
			envelope.error ??
			(typeof envelope.details === "string" ? envelope.details : null) ??
			`HTTP ${response.status}`;
		const apiError = new ApiError(
			message,
			envelope.code ?? "HTTP_ERROR",
			response.status,
			envelope.details ?? null,
			envelope.trace_id,
		);

		// Session expired on any endpoint other than login itself → bounce to
		// the login page. The login page handles its own 401 rendering; navigating
		// to /login while already there caused a hydration/request loop under
		// SvelteKit 3, which refreshes links to the current route.
		if (
			apiError.isAuthError() &&
			browser &&
			!endpoint.startsWith("/auth/login") &&
			window.location.pathname !== "/login"
		) {
			const { goto } = await import("$app/navigation");
			await goto("/login");
		}
		throw apiError;
	}

	if (response.status === 204) {
		return undefined as T;
	}
	return (await response.json()) as T;
}

export function get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
	return api<T>(endpoint, options, "GET");
}

export function post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return api<T>(endpoint, options, "POST", body);
}

export function put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return api<T>(endpoint, options, "PUT", body);
}

export function patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return api<T>(endpoint, options, "PATCH", body);
}

export function del<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
	return api<T>(endpoint, options, "DELETE", body);
}

/** GET returning a Blob (CSV/PDF downloads). */
export async function getBlob(endpoint: string): Promise<Blob> {
	const response = await fetch(`${API_BASE_URL}${endpoint}`, {
		method: "GET",
		credentials: "include",
	});
	if (!response.ok) {
		const envelope = (await response.json().catch(() => ({}))) as ErrorEnvelope;

		throw new ApiError(
			envelope.message ?? `HTTP ${response.status}`,
			envelope.code ?? "HTTP_ERROR",
			response.status,
			envelope.details ?? null,
			envelope.trace_id,
		);
	}
	return response.blob();
}

/** A binary download plus the filename suggested by the server. */
export interface BlobDownload {
	blob: Blob;
	filename: string;
}

/**
 * Reads the `filename` parameter out of a Content-Disposition header. Handles
 * both the plain `filename="a.zip"` form the backend emits and the RFC 5987
 * `filename*=UTF-8''a.zip` form.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
	if (!header) return null;

	const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
	if (encoded?.[1]) {
		const raw = encoded[1].trim().replace(/^"|"$/g, "");
		try {
			return decodeURIComponent(raw);
		} catch {
			return raw;
		}
	}

	const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
	return plain?.[1]?.trim() ?? null;
}

/**
 * POST that returns a Blob (streamed export/archive downloads) together with
 * the server-suggested filename. A plain link cannot trigger these because the
 * request has to be a POST.
 */
export async function postBlob(
	endpoint: string,
	body?: unknown,
	options?: RequestOptions,
): Promise<BlobDownload> {
	const url = `${API_BASE_URL}${endpoint}`;
	const csrfToken = getCsrfToken();

	const headers: Record<string, string> = {
		Accept: "application/zip, application/octet-stream",
		...(body !== undefined ? { "Content-Type": "application/json" } : {}),
		...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
		...(options?.headers ?? {}),
	};

	let response: Response;
	try {
		response = await fetch(url, {
			method: "POST",
			headers,
			credentials: "include",
			signal: options?.signal,
			...(body !== undefined ? { body: JSON.stringify(body) } : {}),
		});
	} catch (err) {
		if ((err as Error).name === "AbortError") throw err;
		throw ApiError.networkError();
	}

	if (!response.ok) {
		const envelope = (await response
			.json()
			.catch(() => ({ message: `HTTP ${response.status}` }))) as ErrorEnvelope;
		const message =
			envelope.message ??
			envelope.error ??
			(typeof envelope.details === "string" ? envelope.details : null) ??
			`HTTP ${response.status}`;
		const apiError = new ApiError(
			message,
			envelope.code ?? "HTTP_ERROR",
			response.status,
			envelope.details ?? null,
			envelope.trace_id,
		);

		if (apiError.isAuthError() && browser) {
			const { goto } = await import("$app/navigation");
			await goto("/login");
		}
		throw apiError;
	}

	return {
		blob: await response.blob(),
		filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")) ?? "",
	};
}

/** POST with a FormData body (file uploads), returning parsed JSON. */
export function postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
	return api<T>(endpoint, undefined, "POST", formData);
}

export { API_BASE_URL };
